import React, { useRef, useState, useCallback } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

/**
 * Renders children inside a Radix Tooltip — but the tooltip only opens if the
 * children element is actually truncated (scrollWidth > clientWidth for
 * single-line truncate, or scrollHeight > clientHeight for line-clamp).
 *
 * Truncation is re-measured every time the user points at the element, so it
 * adapts to viewport resizes, column width changes, dynamic content, etc.
 *
 * Usage:
 *   <TruncatedTooltip content={<FullDetails />}>
 *     <h4 className="truncate">{title}</h4>
 *   </TruncatedTooltip>
 */
export const TruncatedTooltip = ({
  children,
  content,
  side = 'bottom',
  align = 'start',
  className = 'max-w-md bg-slate-900 text-white p-3 text-xs leading-relaxed',
  delayDuration = 0,
}) => {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);

  const isTruncated = useCallback(() => {
    const el = ref.current;
    if (!el) return false;
    const check = (node) => (
      node.scrollWidth > node.clientWidth + 1 ||
      node.scrollHeight > node.clientHeight + 1
    );
    if (check(el)) return true;
    // Also inspect descendants — helpful when the trigger is a wrapper <div>
    // and the actual truncation happens on a nested `truncate` / `line-clamp-*`
    // element (e.g. h4 + p inside a card row).
    const descendants = el.querySelectorAll('*');
    for (const node of descendants) {
      if (check(node)) return true;
    }
    return false;
  }, []);

  const handleOpenChange = (next) => {
    if (next && !isTruncated()) return; // suppress opening when fully visible
    setOpen(next);
  };

  // Clone child to attach our ref without forcing consumer to forward it.
  const trigger = React.cloneElement(children, {
    ref: (node) => {
      ref.current = node;
      const { ref: origRef } = children;
      if (typeof origRef === 'function') origRef(node);
      else if (origRef && typeof origRef === 'object') origRef.current = node;
    },
  });

  return (
    <TooltipProvider delayDuration={delayDuration}>
      <Tooltip open={open} onOpenChange={handleOpenChange}>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent side={side} align={align} className={className}>
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default TruncatedTooltip;
